import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel, Kpi } from "@/components/Widgets";
import { API, useApi, nf, errMsg, df } from "@/lib/api";

const SUGGESTED = ["airbnbitalia", "hostingtips", "bnbitalia", "affittibrevi", "casevacanze", "superhost", "extralberghiero", "intelligenzaartificiale"];

const Result = ({ r }) => (
  <div className="space-y-4" data-testid="hashtag-result">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Kpi testid="ht-avg-likes" label="Like medi top post" value={nf(r.avg_likes)} />
      <Kpi testid="ht-avg-comments" label="Commenti medi" value={nf(r.avg_comments)} />
      <Kpi testid="ht-reel-share" label="Quota video/reel" value={`${Math.round(r.reel_share * 100)}%`} />
      <Kpi testid="ht-competition" label="Competizione" value={r.competition} accent />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel title="Hashtag correlati" sub="Co-occorrenze nei top post">
        <div className="flex flex-wrap gap-2">{r.related.map((t) => <span key={t.tag} className="border border-[#E8E8E4] px-2.5 py-1 text-xs">#{t.tag} <span className="font-mono-num text-[#5C5C58]">{t.count}</span></span>)}</div>
      </Panel>
      <Panel title={`Top post per #${r.hashtag}`}>
        <ul className="divide-y divide-[#E8E8E4]">{r.top_posts.map((p, i) => (
          <li key={i} className="py-2 text-sm flex justify-between gap-3"><a href={p.permalink} target="_blank" rel="noreferrer" className="truncate hover:text-[#2A4B3C]">{p.caption || "(senza testo)"}</a><span className="font-mono-num text-xs whitespace-nowrap">{nf(p.likes)} like</span></li>
        ))}</ul>
      </Panel>
    </div>
  </div>
);

export default function Hashtags() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const hist = useApi("/hashtags/history");
  const search = async (term) => {
    const t = (term ?? q).trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/hashtags/search`, { params: { q: t } });
      setRes(r.data);
      setQ(r.data.hashtag);
      hist.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <PageHeader eyebrow="Instagram Hashtag Search · max 30 hashtag unici / 7 giorni" title="Hashtag & trend" desc="Analizza gli hashtag della tua nicchia: quanto rendono i top post, quanta competizione c'è e con quali altri hashtag vengono usati. Le ricerche alimentano la Strategia AI." />
      <Panel className="mb-4" testid="hashtag-search-panel">
        <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-2">
          <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C58]">#</span>
            <Input data-testid="hashtag-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="es. airbnbitalia" className="pl-7 rounded-none h-11" /></div>
          <Button data-testid="hashtag-search-btn" disabled={busy} className="rounded-none h-11 bg-[#2A4B3C] hover:bg-[#36604C] px-6">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}Analizza</Button>
        </form>
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="label self-center mr-1">Suggeriti</span>
          {SUGGESTED.map((s) => <button key={s} data-testid={`suggested-${s}`} onClick={() => search(s)} className="text-xs border border-[#E8E8E4] px-2.5 py-1 hover:border-[#2A4B3C] hover:text-[#2A4B3C] transition-colors">#{s}</button>)}
        </div>
      </Panel>
      {res && <Result r={res} />}
      {!!hist.data?.length && (
        <Panel title="Ricerche recenti" className="mt-4" testid="hashtag-history">
          <div className="flex flex-wrap gap-2">{hist.data.map((h) => <button key={h.id} data-testid={`history-tag-${h.hashtag}`} onClick={() => setRes(h.result)} className="text-xs border border-[#E8E8E4] px-2.5 py-1 hover:bg-[#F7F7F5]">#{h.hashtag} <span className="text-[#5C5C58]">· {df(h.created_at)}</span></button>)}</div>
        </Panel>
      )}
    </>
  );
}
